<script setup>
import { ref, computed, useId, onMounted, onUnmounted, nextTick } from 'vue';
import CloseIcon from '@/js/core/components/icons/CloseIcon.vue';
import { useTimezoneStore } from '@/js/core/stores/timezone-store';
import { useFocusTrap } from '@/js/core/composables/useFocusTrap';
import { useAriaHiddenBackground } from '@/js/core/composables/useAriaHiddenBackground';
import { useActiveScroller } from '@/js/core/composables/useActiveScroller';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  ariaLabel: {
    type: String,
    default: '',
  },
  showClose: {
    type: Boolean,
    default: true,
  },
  wrapperClass: {
    type: String,
    default: '',
  },
  moveFocusOnMount: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const timezoneStore = useTimezoneStore();

const titleId = useId();

const modalRoot = ref(null);
const scrollRegionRef = ref(null);
const scrollAreaRef = ref(null);

const { activeScrollerRef, isScrollable } = useActiveScroller(scrollRegionRef, scrollAreaRef);
// Ordered so the background is revealed again before the focus trap returns
// focus to the element that opened the dialog.
useAriaHiddenBackground(modalRoot);
// Route modals keep focus where it is on open: the rest of the page is
// aria-hidden, VoiceOver scopes Read All to whatever it lands on, and a forced
// focus target also shows a focus ring. Nested non-route dialogs (e.g. the
// comment media viewer) opt in via moveFocusOnMount because the trigger they
// leave focus on is aria-hidden while they are open.
useFocusTrap(modalRoot, { moveFocusOnMount: props.moveFocusOnMount });

const activeScrollerLabel = computed(() => {
  // Title may carry editor HTML (rendered via v-html); strip tags for the label.
  const plainTitle = props.title.replace(/<[^>]*>/g, '').trim();
  return plainTitle ? `${plainTitle} content` : 'Modal content';
});

const scrollerA11yAttrs = el =>
  isScrollable.value && activeScrollerRef.value === el
    ? { tabindex: 0, role: 'region', 'aria-label': activeScrollerLabel.value }
    : {};

const close = () => {
  emit('close');
};

const handleKeydown = event => {
  if (event.key === 'Escape') {
    close();
  }
};

// Track which body classes this instance added, so a nested modal closing
// doesn't strip state an outer modal still relies on.
let addedModalOpenClass = false;
let addedViewModalClass = false;

onMounted(() => {
  if (!document.body.classList.contains('modal-open')) {
    document.body.classList.add('modal-open');
    addedModalOpenClass = true;
  }
  document.addEventListener('keydown', handleKeydown);

  if (
    document.querySelector('.view-tray-modal__content') &&
    !document.body.classList.contains('view-modal')
  ) {
    document.body.classList.add('view-modal');
    addedViewModalClass = true;
  }

  nextTick(() => {
    const currentTime = timezoneStore.currentTime;
    const hour = currentTime.getHours();
    const isDay = hour >= 6 && hour < 18;

    // Course modal places the day/night flag on the wrapper so the gradient sits there;
    // every other modal keeps it on the container.
    const courseWrapper = document.querySelector(
      '.full-page-modal.is-course-modal .full-page-modal__container-wrapper'
    );
    const primaryTarget = courseWrapper || document.querySelector('.full-page-modal__container');

    // The event modal carries the gradient on its close bar too, so flag it as well.
    const eventCloseContainer = document.querySelector(
      '.full-page-modal.is-event-modal .full-page-modal__close-container'
    );

    [primaryTarget, eventCloseContainer].forEach(target => {
      if (!target) return;
      if (isDay) {
        target.setAttribute('data-day', '');
        target.removeAttribute('data-night');
      } else {
        target.setAttribute('data-night', '');
        target.removeAttribute('data-day');
      }
    });
  });
});

onUnmounted(() => {
  if (addedModalOpenClass) {
    document.body.classList.remove('modal-open');
  }
  document.removeEventListener('keydown', handleKeydown);

  if (addedViewModalClass) {
    document.body.classList.remove('view-modal');
  }
});

const formatDescription = text => {
  if (!text) return '';

  // 1. If it already contains HTML tags (like <p>), just return it
  if (/<[a-z][\s\S]*>/i.test(text)) return text;

  // 2. Otherwise, split by double newlines and wrap in <p> tags
  return text
    .split(/\n\s*\n/)
    .map(para => `<p>${para.replace(/\n/g, '<br>')}</p>`)
    .join('');
};
</script>

<template>
  <div
    ref="modalRoot"
    class="full-page-modal"
    :class="wrapperClass"
    role="dialog"
    :aria-labelledby="title ? titleId : undefined"
    :aria-label="!title ? ariaLabel || undefined : undefined"
    tabindex="-1"
  >
    <div class="full-page-modal__backdrop" @click="close" />
    <div class="full-page-modal__container-wrapper">
      <div class="full-page-modal__container">
        <div
          ref="scrollRegionRef"
          class="full-page-modal__scroll-region"
          v-bind="scrollerA11yAttrs(scrollRegionRef)"
        >
          <div class="full-page-modal__close-container">
            <div class="full-page-modal__close-case">
              <button
                v-if="showClose"
                type="button"
                class="full-page-modal__close"
                aria-label="Close modal"
                @click="close"
              >
                <div class="full-page-modal__close-wrapper"><CloseIcon /></div>
              </button>
            </div>
          </div>

          <div
            ref="scrollAreaRef"
            class="full-page-modal__scroll-area"
            v-bind="scrollerA11yAttrs(scrollAreaRef)"
          >
            <div v-if="$slots['header-top']" class="full-page-modal__header-top">
              <slot name="header-top" />
            </div>

            <h1
              v-if="title"
              :id="titleId"
              class="full-page-modal__title"
              v-html="formatDescription(title)"
            ></h1>

            <div class="full-page-modal__content">
              <slot />
            </div>
          </div>
        </div>

        <footer v-if="$slots.footer" class="full-page-modal__footer">
          <div class="full-page-modal__footer-inner">
            <slot name="footer" />
          </div>
        </footer>
      </div>
    </div>

    <!--
    Slot for People modal overlay.
    -->
    <slot name="overlay" />
  </div>
</template>
